import React, {ReactElement} from 'react'
import {Stack, HStack, Text, useMultiStyleConfig, StackProps, TextProps, Link, LinkProps, useDisclosure, Box} from '@chakra-ui/react'
import {IconButton, Icon} from '_uiComponents'
import {FooterSocial, FooterSocialId, FooterSocials} from '_types'
import WeChatQRCode from './partials/wechat-qrcode'
import {useViewport} from '_customHooks'

/**
 * @name FooterSocials
 *
 * @component
 *
 * @description
 * Renders a title and a list of social buttons.
 *
 * @param {FooterSocials} props - The properties object.
 * @param {string} props.title - The title of the list.
 * @param {Array<FooterSocial>} props.list - A list of items.
 *
 * @returns {React.FC<FooterSocials>} - Rendered `FooterSocialsComponent`
 */
const FooterSocialsComponent: React.FC<FooterSocials> = ({title, list}) => {
    const styles = useMultiStyleConfig('FooterSocials') as Record<string, StackProps & TextProps & LinkProps>
    const {isMobile} = useViewport()
    const {isOpen: showQRCode, onOpen: onQRCodeShow, onClose: onQRCodeHide} = useDisclosure()

    const MapSocial = new Map<FooterSocialId, ReactElement>([
        [FooterSocialId.Instagram, <Icon name="IconInstagram" size="xl" key={FooterSocialId.Instagram} />],
        [FooterSocialId.Facebook, <Icon name="IconFacebook" size="xl" key={FooterSocialId.Facebook} />],
        [FooterSocialId.Linkedin, <Icon name="IconLinkedin" size="xl" key={FooterSocialId.Linkedin} />],
        [FooterSocialId.Youtube, <Icon name="IconYoutube" size="xl" key={FooterSocialId.Youtube} />],
        [FooterSocialId.Pinterest, <Icon name="IconPinterest" size="xl" key={FooterSocialId.Pinterest} />],
        [FooterSocialId.Tiktok, <Icon name="IconTiktok" size="xl" key={FooterSocialId.Tiktok} />],
        [FooterSocialId.Spotify, <Icon name="IconSpotify" size="xl" key={FooterSocialId.Spotify} />],
        [FooterSocialId.WeChat, <Icon name="IconWeChat" size="xl" key={FooterSocialId.WeChat} />],
        [FooterSocialId.Weibo, <Icon name="IconWeibo" size="xl" key={FooterSocialId.Weibo} />],
    ])

    return (
        <Stack {...styles.section} data-testid="footer-socials">
            <Text as="h2" {...styles.title}>
                {title}
            </Text>
            <HStack {...styles.socialList}>
                {list.map((social: FooterSocial) => (
                    <Box
                        key={social.name}
                        onMouseEnter={() => (social.id as FooterSocialId) === FooterSocialId.WeChat && onQRCodeShow()}
                        onMouseLeave={() => (social.id as FooterSocialId) === FooterSocialId.WeChat && onQRCodeHide()}
                    >
                        <IconButton
                            size="iconButtonMd"
                            aria-label={social.name}
                            icon={MapSocial.get(social.id as FooterSocialId) ?? <></>}
                            data-testid={`social-icon-button-${social.id}`}
                            position={(social.id as FooterSocialId) === FooterSocialId.WeChat ? 'relative' : 'initial'}
                            as={Link}
                            /*@ts-expect-error -- Icon button is using `as` directive here*/
                            href={social.href}
                            isExternal
                        />
                        {!isMobile && (social.id as FooterSocialId) === FooterSocialId.WeChat && <>{showQRCode && <WeChatQRCode />}</>}
                    </Box>
                ))}
            </HStack>
        </Stack>
    )
}

export default FooterSocialsComponent
